/**
 * GlobalConversationSidebar - Conversation list for the global chrome.
 *
 * Shows all conversations (Word + Workspace) in a single flat list sorted by
 * last activity. A compact "Filter" button on the right lets the user narrow
 * by dossier, show archived conversations, and narrow by date. Each item
 * carries a top-right slot that shows the source badge by default and the
 * actions menu (organization + owner actions) on hover.
 *
 * Presentational only: it receives the list + active id + callbacks. It lives
 * inside AppSidebar (fixed-width chrome), so it has no width/resize/collapse
 * logic of its own — it simply fills its parent.
 */

import { useState, useMemo, useEffect, useCallback, memo } from "react";
import { useMatch } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  MessageCircle,
  MoreVertical,
  Trash2,
  Pencil,
  Search,
  Briefcase,
  Loader2,
  Share2,
  Pin,
  PinOff,
  Archive,
  ArchiveRestore,
  PenLine,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Input } from "@/components/ui/input";
import type { Conversation } from "@/types/chat";
import type { ConversationShare } from "@hector/shared-types/resource-sharing";
import { useCabinetId } from "@/hooks/useCabinetMemberMe";
import { useAuth } from "@/components/AuthProvider";
import { useCabinetMembers } from "@/hooks/cabinet/useCabinetMembers";
import type { EnrichedCabinetMember } from "@/hooks/cabinet/types";
import { ShareConversationPopover } from "@/components/case/ShareConversationPopover";
import { ShareParticipantsGlyph, type ShareParticipant } from "@/components/sharing/ShareParticipantsGlyph";
import { useActiveSessionsStore } from '../../stores/activeSessionsStore';
import { canvasDocumentRuntime } from '@/features/redaction/state/canvasDocumentRuntime';
import type { CanvasSessionSnapshot } from '@/features/redaction/state/canvasSessionRegistry';
import { isCanvasSessionWritingByAgent } from '@/features/redaction/state/canvasAgentWriting';
import { shallow } from 'zustand/shallow';
import { formatAdaptiveTimestamp } from '../../utils/formatTimestamp';
import type { SessionStatus, TimelineItem } from '../../types/session.types';
import { useInlineRename } from "@/hooks/useInlineRename";
import {
  matchesDateFilter,
  useHistoryFilters,
  type ConversationStatusFilter,
} from '../../hooks/useHistoryFilters';
import { HistoryFilterButton } from './HistoryFilterButton';
import { ConversationSourceBadge } from './ConversationSourceBadge';
import { compareConversationProjectionOrder } from '@/utils/conversationProjection';

export interface GlobalConversationSidebarProps {
  /** Conversations dont je suis l'owner — liste principale. */
  conversations: Conversation[];
  /** Conversations partagées avec moi (`viewer`/`editor`) — fusionnées dans la timeline. */
  sharedConversations?: Conversation[];
  activeConversationId: string | null;
  isLoading: boolean;
  onConversationSelect: (conversationId: string) => void;
  onConversationDelete: (conversationId: string) => void;
  onConversationRename: (conversationId: string, newTitle: string) => Promise<void>;
  /** Personal organization actions remain available for viewer/editor rows. */
  onConversationPinChange: (conversationId: string, pinned: boolean) => Promise<void>;
  onConversationArchiveChange: (conversationId: string, archived: boolean) => Promise<void>;
}

/**
 * Returns a map of conversationId → SessionStatus for sessions that are
 * actively doing work (excludes idle and done to avoid badge noise).
 *
 * Uses manual `.subscribe()` instead of a Zustand selector to avoid the
 * "getSnapshot should be cached" infinite loop: the sessions slice is
 * updated at ~50 Hz during streaming, and a selector that returns a new
 * object on each call breaks React's `useSyncExternalStore` contract.
 * Manual subscribe + `shallow` comparison ensures stable references.
 */
/** The point describes the agent turn; the Canvas pencil is a separate axis. */
type BadgeType = SessionStatus | 'waiting_action';
type CanvasIndicator = 'ready' | 'writing';

const EMPTY_BADGES: Record<string, BadgeType> = {};
const EMPTY_CANVAS_INDICATORS: Record<string, CanvasIndicator> = {};

function computeBadges(sessions: Record<string, { conversationId: string | null; status: SessionStatus; timeline: TimelineItem[] }>): Record<string, BadgeType> {
  const result: Record<string, BadgeType> = {};
  let hasAny = false;
  for (const session of Object.values(sessions)) {
    // WHY skip 'error': we don't surface an error dot in the conversation list.
    // The error is already shown inline in the conversation (agent_error item); a
    // persistent red dot in the sidebar is redundant and reads as anxiety-inducing
    // "something is broken" long after a recoverable error. Live-progress badges
    // (streaming / connecting / waiting_action) stay — they're actionable. (2026-06-26)
    if (session.conversationId && session.status !== 'ready' && session.status !== 'error') {
      const hasJpForm = session.status === 'streaming'
        && session.timeline.some((item) => item.type === 'jurisprudence_form');
      result[session.conversationId] = hasJpForm ? 'waiting_action' : session.status;
      hasAny = true;
    }
  }
  return hasAny ? result : EMPTY_BADGES;
}

function useSessionBadges(): Record<string, BadgeType> {
  const [badges, setBadges] = useState<Record<string, BadgeType>>(EMPTY_BADGES);

  useEffect(() => {
    setBadges(computeBadges(useActiveSessionsStore.getState().sessions));

    return useActiveSessionsStore.subscribe((state) => {
      const next = computeBadges(state.sessions);
      setBadges((prev) => shallow(prev, next) ? prev : next);
    });
  }, []);

  return badges;
}

/**
 * Projection Canvas intentionnellement distincte de l'activité de tour :
 * l'avocat peut voir les deux faits en même temps sur une ligne.
 */
export function computeCanvasIndicators(
  sessions: readonly CanvasSessionSnapshot[],
): Record<string, CanvasIndicator> {
  const indicators: Record<string, CanvasIndicator> = {};
  for (const session of sessions) {
    // ⚠️ Deux gardes ont disparu avec #1336, et toutes deux étaient devenues
    // inatteignables : `conversationId` n'est plus nullable — une session sans
    // propriétaire ne désignerait rien —, et la dominance « écriture »
    // départageait DEUX snapshots chauds de la même conversation, ce que le
    // registre indexé par conversation ne peut plus produire.
    if (session.temperature !== 'hot') continue;
    // Ces compteurs sont alimentés par les chemins Hector seulement : l'édition
    // avocat et l'autosave ne deviennent jamais une fausse « écriture agent ».
    indicators[session.conversationId] = isCanvasSessionWritingByAgent(session) ? 'writing' : 'ready';
  }
  return Object.keys(indicators).length === 0 ? EMPTY_CANVAS_INDICATORS : indicators;
}

export function getCanvasIndicatorPresentation(
  indicator: CanvasIndicator | undefined,
): { className: string; ariaLabel: string } | null {
  if (!indicator) return null;
  return {
    className: cn(
      "h-3 w-3 flex-shrink-0 text-[#003D82]",
      indicator === 'writing' && 'animate-pulse',
    ),
    ariaLabel: indicator === 'writing' ? 'Hector modifie le Canvas' : 'Canvas chargé',
  };
}

function useCanvasIndicators(): Record<string, CanvasIndicator> {
  const [indicators, setIndicators] = useState<Record<string, CanvasIndicator>>(
    () => computeCanvasIndicators(canvasDocumentRuntime.getSessionsSnapshot().sessions),
  );

  useEffect(() => {
    const synchronize = (snapshot = canvasDocumentRuntime.getSessionsSnapshot()): void => {
      const next = computeCanvasIndicators(snapshot.sessions);
      setIndicators((previous) => shallow(previous, next) ? previous : next);
    };
    // Subscribe first, then reread: no hot/ready transition can be lost in the
    // render→effect gap, including the final transition of a background hydrate.
    const unsubscribe = canvasDocumentRuntime.subscribeSessions(synchronize);
    synchronize();
    return unsubscribe;
  }, []);

  return indicators;
}

/**
 * Timeline unique de la sidebar : conversations owned + partagées, sans section
 * séparée. Un partage reçu est une propriété visuelle/filtrable, pas un tiroir en
 * bas de liste, sinon les fils récents partagés deviennent invisibles au scroll.
 */
export function mergeConversationsForTimeline(
  conversations: Conversation[],
  sharedConversations: Conversation[],
  status: ConversationStatusFilter = 'active',
): Conversation[] {
  const allConversations = mergeConversationRecords(conversations, sharedConversations);
  const shouldBeArchived = status === 'archived';

  return allConversations
    .filter((conversation) => conversation.isArchived === shouldBeArchived)
    .sort(compareConversationProjectionOrder);
}

function mergeConversationRecords(
  conversations: Conversation[],
  sharedConversations: Conversation[],
): Conversation[] {
  const byId = new Map<string, Conversation>();
  for (const conversation of conversations) byId.set(conversation.id, conversation);
  for (const conversation of sharedConversations) byId.set(conversation.id, conversation);
  return Array.from(byId.values());
}

export function GlobalConversationSidebar({
  conversations,
  sharedConversations = [],
  activeConversationId,
  isLoading,
  onConversationSelect,
  onConversationDelete,
  onConversationRename,
  onConversationPinChange,
  onConversationArchiveChange,
}: GlobalConversationSidebarProps) {
  const sessionBadges = useSessionBadges();
  const canvasIndicators = useCanvasIndicators();
  const { filters, setDate, setCase, setStatus, reset, isFiltered } = useHistoryFilters();
  const { user } = useAuth();
  const currentUserId = user?.id ?? null;

  // Membres du cabinet — résout les avatars/noms des destinataires du partage
  // affichés par ligne (`ConversationShareGlyph`). Fetch une seule fois ici
  // (query cachée par TanStack) plutôt que par ligne memoïsée.
  const cabinetId = useCabinetId();
  const { data: cabinetMembers } = useCabinetMembers(cabinetId);

  // Dossier courant (route `/cases/:id` et sous-pages). Sert à GROUPER la liste — les
  // conversations de ce dossier remontent en tête (cf. plus bas) — au lieu de la filtrer
  // dur : on garde ainsi l'accès aux autres conversations et la liste n'est jamais vide.
  // Le filtre PAR dossier reste disponible explicitement via le bouton « Filtrer »
  // (`filters.caseId`), qui, lui, masque les autres.
  const caseRouteExact = useMatch("/cases/:id");
  const caseRouteSub = useMatch("/cases/:id/*");
  const routeCaseId = caseRouteExact?.params.id ?? caseRouteSub?.params.id ?? null;

  const timelineConversations = useMemo(
    () => mergeConversationsForTimeline(conversations, sharedConversations, filters.status),
    [conversations, sharedConversations, filters.status],
  );
  const allConversations = useMemo(
    () => mergeConversationRecords(conversations, sharedConversations),
    [conversations, sharedConversations],
  );
  const archivedCount = useMemo(
    () => allConversations.filter((conversation) => conversation.isArchived).length,
    [allConversations],
  );

  // Dossier options for the filter are DERIVED from the loaded conversations
  // (deduped caseId → caseTitle), not from all cabinet cases: only dossiers that
  // actually have a conversation can yield a result, and this needs no extra query.
  const caseOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of allConversations) {
      if (c.caseId && c.caseTitle) map.set(c.caseId, c.caseTitle);
    }
    return Array.from(map, ([id, title]) => ({ id, title })).sort((a, b) =>
      a.title.localeCompare(b.title),
    );
  }, [allConversations]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchDialog, setShowSearchDialog] = useState(false);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  /**
   * Prédicat de filtrage date + recherche + dossier appliqué à la
   * timeline unique (owned + partagées).
   */
  const passesFilters = useCallback(
    (c: Conversation, now: Date): boolean => {
      const byDate = matchesDateFilter(
        (c.lastMessageAt ?? c.updatedAt ?? c.createdAt).toString(),
        filters.date,
        now,
      );
      const q = searchQuery.toLowerCase();
      const bySearch =
        !searchQuery
        || c.title.toLowerCase().includes(q)
        || (c.caseTitle?.toLowerCase().includes(q) ?? false);
      const byCase = !filters.caseId || c.caseId === filters.caseId;
      return byDate && bySearch && byCase;
    },
    [filters.date, filters.caseId, searchQuery],
  );

  /**
   * Flat sorted list: filter by date + dossier + search, then sort by
   * lastMessageAt desc. Replaces the previous date-grouped layout — the
   * date filter dimension lives in the HistoryFilterButton menu.
   */
  const filteredConversations = useMemo(() => {
    const now = new Date();
    return timelineConversations.filter((c) => passesFilters(c, now));
  }, [timelineConversations, passesFilters]);

  // Age-scaled label (today → HH:mm, else day/month, prior years → +year),
  // identical to the chat bubbles. See utils/formatTimestamp.
  const formatShortDate = formatAdaptiveTimestamp;

  // Groupement par dossier courant (route), sauf si un filtre dossier manuel est actif.
  // Conv du dossier en tête (ordre canonique déjà appliqué), puis les autres. Si le
  // dossier n'a aucune conv → pas de groupes,
  // liste plate (donc non vide) ; on peut toujours naviguer vers d'autres dossiers.
  const groupByDossier = !!routeCaseId && !filters.caseId;
  const dossierConvs = useMemo(
    () =>
      groupByDossier
        ? filteredConversations
            .filter((c) => c.caseId === routeCaseId)
        : [],
    [groupByDossier, filteredConversations, routeCaseId],
  );
  const otherConvs = useMemo(
    () =>
      groupByDossier
        ? filteredConversations.filter((c) => c.caseId !== routeCaseId)
        : filteredConversations,
    [groupByDossier, filteredConversations, routeCaseId],
  );
  const showGroups = groupByDossier && dossierConvs.length > 0;

  // Callback stable : sans ça, `() => setRenamingId(null)` recrée une fonction à
  // chaque render → casse le React.memo de ConversationRow → toutes les lignes
  // re-render à chaque update de sessionBadges (50+ fois/s pendant le streaming).
  const handleFinishRename = useCallback(() => setRenamingId(null), []);

  const renderRow = (conv: Conversation, accent = false) => (
    <ConversationRow
      key={conv.id}
      conv={conv}
      accent={accent}
      isActive={conv.id === activeConversationId}
      isRenaming={renamingId === conv.id}
      badge={sessionBadges[conv.id]}
      canvasIndicator={canvasIndicators[conv.id]}
      formatShortDate={formatShortDate}
      onSelect={onConversationSelect}
      onStartRename={setRenamingId}
      onFinishRename={handleFinishRename}
      onRequestDelete={setPendingDeleteId}
      onRename={onConversationRename}
      onPinChange={onConversationPinChange}
      onArchiveChange={onConversationArchiveChange}
      members={cabinetMembers}
      currentUserId={currentUserId}
    />
  );

  return (
    <TooltipProvider>
    <div className="flex flex-col h-full min-h-0">
      {/* Section header — "Mes conversations", left-aligned with the conversation titles
          below (list p-2 + row px-2.5 = 18px). Placé AU-DESSUS de la recherche. */}
      <div className="px-[18px] pt-2 pb-1">
        <span className="text-[10px] font-medium uppercase tracking-wide text-grey-600">
          Mes conversations
        </span>
      </div>

      {/* Header — Search + Filter on one row: Rechercher fills, Filter is icon-only
          on the right. ("Nouvelle conversation" lives in AppSidebar's nav.) */}
      <div className="px-2 pb-1">
        <div className="flex items-center gap-1">
          <Button
            onClick={() => setShowSearchDialog(true)}
            variant="ghost"
            className="flex-1 text-grey-600 hover:text-charcoal hover:bg-[#F0F0EE] justify-start font-normal h-7 text-[13px] px-2.5"
          >
            <Search className="w-3 h-3 min-w-[12px]" />
            <span className="ml-1.5">Rechercher</span>
          </Button>
          <HistoryFilterButton
            filters={filters}
            onDateChange={setDate}
            onCaseChange={setCase}
            onStatusChange={setStatus}
            onReset={reset}
            caseOptions={caseOptions}
            archivedCount={archivedCount}
            isFiltered={isFiltered}
          />
        </div>
      </div>

      {/* Conversations List — flat, sorted by last_activity desc */}
      <div className="flex-1 overflow-y-auto p-2 scrollbar-thin scrollbar-thumb-[#969691] scrollbar-track-transparent">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-8 text-grey-600">
            <Loader2 className="w-4 h-4 animate-spin mb-2" />
            <span className="text-xs">Chargement...</span>
          </div>
        ) : (
          <>
            {/* Timeline unique : mes fils + fils partagés avec moi, par activité. */}
            {filteredConversations.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-grey-600">
                <MessageCircle className="w-4 h-4 mb-2 opacity-50" />
                <span className="text-sm font-medium">
                  {timelineConversations.length === 0
                    ? filters.status === 'archived'
                      ? 'Aucune conversation archivée'
                      : 'Aucune conversation active'
                    : filters.status === 'archived'
                      ? 'Aucune conversation archivée pour ces filtres'
                      : 'Aucune conversation active pour ces filtres'}
                </span>
                {timelineConversations.length === 0 && filters.status === 'active' && (
                  <span className="text-xs mt-1">Commencez une nouvelle discussion</span>
                )}
              </div>
            ) : showGroups ? (
              // Dossier courant : ses conversations en tête (accent), puis les autres.
              <div className="space-y-0.5">
                <div className="px-2.5 pt-1 pb-1 text-[10px] font-medium uppercase tracking-wide text-grey-500">
                  Dans ce dossier
                </div>
                {dossierConvs.map((conv) => renderRow(conv, true))}
                {otherConvs.length > 0 && (
                  <div className="px-2.5 pt-3 pb-1 text-[10px] font-medium uppercase tracking-wide text-grey-500">
                    Autres conversations
                  </div>
                )}
                {otherConvs.map((conv) => renderRow(conv))}
              </div>
            ) : (
              <div className="space-y-0.5">
                {filteredConversations.map((conv) => renderRow(conv))}
              </div>
            )}

            {filteredConversations.length === 0 && sharedConversations.length > 0 && isFiltered && (
              <div className="mt-2 px-2.5 py-2 text-[11px] leading-snug text-grey-500">
                Des conversations partagées sont masquées par le filtre actif.
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete confirmation */}
      <AlertDialog
        open={pendingDeleteId !== null}
        onOpenChange={(open) => { if (!open) setPendingDeleteId(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette conversation ?</AlertDialogTitle>
            <AlertDialogDescription>
              Tous les messages de cette conversation seront définitivement perdus. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingDeleteId) {
                  onConversationDelete(pendingDeleteId);
                  setPendingDeleteId(null);
                }
              }}
              className="bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-600"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Search Dialog */}
      <Dialog open={showSearchDialog} onOpenChange={(open) => {
        setShowSearchDialog(open);
        if (!open) {
          setSearchQuery("");
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Search className="w-4 h-4" />
              Rechercher une conversation
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 min-w-0">
            <Input
              placeholder="Titre ou nom du dossier..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-10 text-sm"
              autoFocus
            />
            <div className="mt-4 max-h-72 overflow-y-auto space-y-1 min-w-0 px-1 scrollbar-thin scrollbar-thumb-[#969691] scrollbar-track-transparent">
              {/* La recherche couvre la timeline unique : miennes + partagées. */}
              {filteredConversations.length === 0 ? (
                <div className="text-center py-6 text-[#666666] text-sm">
                  Aucun résultat
                </div>
              ) : (
                filteredConversations.map((conv) => (
                  <ConversationRow
                    key={conv.id}
                    conv={conv}
                    roomy
                    isActive={conv.id === activeConversationId}
                    isRenaming={renamingId === conv.id}
                    badge={sessionBadges[conv.id]}
                    canvasIndicator={canvasIndicators[conv.id]}
                    formatShortDate={formatShortDate}
                    onSelect={(id) => {
                      onConversationSelect(id);
                      setShowSearchDialog(false);
                      setSearchQuery("");
                    }}
                    onStartRename={setRenamingId}
                    onFinishRename={() => setRenamingId(null)}
                    onRequestDelete={setPendingDeleteId}
                    onRename={onConversationRename}
                    onPinChange={onConversationPinChange}
                    onArchiveChange={onConversationArchiveChange}
                    members={cabinetMembers}
                    currentUserId={currentUserId}
                  />
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </TooltipProvider>
  );
}

function shareInitials(member: EnrichedCabinetMember | undefined, userId: string): string {
  if (member?.full_name) return member.full_name.slice(0, 2).toUpperCase();
  if (member?.email) return (member.email[0] ?? "?").toUpperCase();
  return (userId[0] ?? "?").toUpperCase();
}

function shareName(member: EnrichedCabinetMember | undefined, userId: string): string {
  return member?.full_name || member?.email || `${userId.slice(0, 8)}…`;
}

/**
 * Glyphe « qui a accès » affiché À GAUCHE du glyphe source (plume / Word) dans
 * une ligne de conversation. Version miniature de la colonne « Partagé à » des
 * dossiers (`SharedWithCell`) : avatars empilés minuscules de TOUS les
 * participants — **l'auteur (owner) en tête** puis les destinataires — cappés
 * (max 3) + badge « +N », ou une petite icône cabinet pour `visibility:cabinet`.
 * Le tooltip de l'auteur porte « (auteur) ».
 *
 * N'affiche RIEN si le fil est privé sans partage. Pour l'owner, `shares`
 * affiche auteur + destinataires. Pour un viewer/editor, `shares` est masqué
 * par le backend (privacy), donc on affiche au moins l'auteur (`ownerId`) pour
 * que la timeline unique ait un repère visuel stable.
 */
export type ConversationShareGlyphParticipant = {
  userId: string;
  role: "author" | "viewer" | "editor";
};

export function buildConversationShareGlyphParticipants({
  myRole,
  ownerId,
  currentUserId,
  shares,
}: {
  myRole: Conversation["my_role"];
  ownerId: Conversation["ownerId"];
  currentUserId?: string | null;
  shares: ConversationShare[] | undefined;
}): ConversationShareGlyphParticipant[] {
  if (!ownerId) return [];

  if (myRole === "viewer" || myRole === "editor") {
    return [
      { userId: ownerId, role: "author" },
      ...(currentUserId ? [{ userId: currentUserId, role: myRole }] : []),
    ];
  }

  if (myRole !== "owner" || !shares || shares.length === 0) {
    return [];
  }

  return [
    { userId: ownerId, role: "author" },
    ...shares.map((s) => ({ userId: s.user_id, role: s.role })),
  ];
}

export function conversationOwnerActionDisabledReason(
  myRole: Conversation["my_role"],
): string | undefined {
  if (myRole === "owner" || myRole === undefined) return undefined;
  if (myRole === "viewer") return "Vous avez seulement accès à cette conversation en lecture seule.";
  return "Vous pouvez écrire dans ce fil, mais seul son auteur peut le renommer ou le supprimer.";
}

function ConversationShareGlyph({
  visibility,
  myRole,
  ownerId,
  currentUserId,
  shares,
  members,
}: {
  visibility: Conversation["visibility"];
  myRole: Conversation["my_role"];
  ownerId: Conversation["ownerId"];
  currentUserId?: string | null;
  shares: ConversationShare[] | undefined;
  members: EnrichedCabinetMember[] | undefined;
}): JSX.Element | null {
  if (visibility === "cabinet") return <ShareParticipantsGlyph participants={[]} visibility="cabinet" size="xs" />;

  const participants = buildConversationShareGlyphParticipants({ myRole, ownerId, currentUserId, shares });
  const renderedParticipants: ShareParticipant[] = participants.map((p) => {
    const member = members?.find((m) => m.user_id === p.userId);
    return {
      userId: p.userId,
      name: shareName(member, p.userId),
      initials: shareInitials(member, p.userId),
      avatarUrl: member?.avatar_url ?? null,
      role: p.role,
    };
  });

  return <ShareParticipantsGlyph participants={renderedParticipants} size="xs" />;
}

function ReadOnlyConversationMenuItem({
  icon,
  label,
  reason,
  destructive = false,
}: {
  icon: JSX.Element;
  label: string;
  reason: string;
  destructive?: boolean;
}): JSX.Element {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <DropdownMenuItem
          onClick={(e) => e.stopPropagation()}
          onSelect={(event) => event.preventDefault()}
          aria-disabled
          className={cn(
            "cursor-not-allowed text-xs opacity-45 focus:bg-transparent",
            destructive ? "text-red-600 focus:text-red-600" : "text-charcoal focus:text-charcoal",
          )}
        >
          {icon}
          {label}
        </DropdownMenuItem>
      </TooltipTrigger>
      <TooltipContent side="left" className="max-w-[240px] text-xs text-grey-600">
        {reason}
      </TooltipContent>
    </Tooltip>
  );
}

interface ConversationRowProps {
  conv: Conversation;
  isActive: boolean;
  isRenaming: boolean;
  badge: BadgeType | undefined;
  canvasIndicator: CanvasIndicator | undefined;
  formatShortDate: (d: Date) => string;
  onSelect: (id: string) => void;
  onStartRename: (id: string) => void;
  onFinishRename: () => void;
  onRequestDelete: (id: string) => void;
  onRename: (id: string, newTitle: string) => Promise<void>;
  onPinChange: (id: string, pinned: boolean) => Promise<void>;
  onArchiveChange: (id: string, archived: boolean) => Promise<void>;
  /** Membres du cabinet (résolution avatar/nom des destinataires du partage). */
  members: EnrichedCabinetMember[] | undefined;
  /** User courant : côté destinataire, affiche auteur + moi sans exposer tous les shares. */
  currentUserId?: string | null;
  /** Slightly taller/wider padding for the search dialog (design otherwise identical). */
  roomy?: boolean;
  /** Conversation du dossier courant : fin liseré d'accent à gauche pour la repérer. */
  accent?: boolean;
}

/**
 * Conversation row — extracted from the parent so `useInlineRename` can live
 * at the top of the component (a hook can't sit inside a conditional render
 * function).
 *
 * Top-right slot: source badge by default, swapped with the actions menu
 * (pin/archive + owner actions) on hover. Same anchor — the user's eye sees a
 * single point of attention per item, not two.
 */
// React.memo : avec des callbacks/props stables (cf. handleFinishRename, setters
// useState, formatAdaptiveTimestamp), une ligne ne se re-render que si SON conv /
// isActive / isRenaming / badge change. Coupe la cascade « toutes les lignes
// re-render à chaque tick de sessionBadges pendant le streaming ». (#238, Batch C)
const ConversationRow = memo(function ConversationRow({
  conv,
  isActive,
  isRenaming,
  badge,
  canvasIndicator,
  formatShortDate,
  onSelect,
  onStartRename,
  onFinishRename,
  onRequestDelete,
  onRename,
  onPinChange,
  onArchiveChange,
  members,
  currentUserId,
  roomy = false,
  accent = false,
}: ConversationRowProps) {
  const displayTitle = conv.title || conv.caseTitle || "Général";

  // Menu d'actions contrôlé : tant qu'il est ouvert on garde le ⋮ visible ET le glyphe
  // source masqué. Sans ça, ouvrir le menu fait quitter le hover de la ligne → le glyphe
  // source réapparaît et se superpose au ⋮ (le hover seul ne suffit pas).
  const [menuOpen, setMenuOpen] = useState(false);
  // Panneau de partage ancré à l'icône Partager (owner-only) — state local à la
  // ligne depuis 2026-07-07 (remplace la modale levée au niveau du parent) :
  // `ShareConversationPopover` a besoin de tout ce que `conv` porte déjà, pas
  // besoin de remonter l'état plus haut.
  const [shareOpen, setShareOpen] = useState(false);
  const ownerActionDisabledReason = conversationOwnerActionDisabledReason(conv.my_role);
  const canvasPresentation = getCanvasIndicatorPresentation(canvasIndicator);

  const rename = useInlineRename({
    initialValue: displayTitle,
    onSave: async (newTitle) => {
      try {
        await onRename(conv.id, newTitle);
      } finally {
        onFinishRename();
      }
    },
    onCancel: onFinishRename,
  });

  return (
    <Tooltip delayDuration={400}>
      <TooltipTrigger asChild>
        <div
          // Navigable au clavier : la liste de conversations est le point d'entrée principal
          // de l'app (RGAA). role=button + tabIndex + Entrée/Espace pour sélectionner.
          // Désactivé pendant le renommage (l'input interne prend le focus).
          role="button"
          tabIndex={isRenaming ? -1 : 0}
          aria-label={`Ouvrir la conversation ${displayTitle}`}
          // Les titres sont éditables et peuvent être identiques. L'UUID de la
          // conversation reste donc l'unique ancre stable pour les preuves E2E
          // de navigation inter-canvas.
          data-conversation-id={conv.id}
          onKeyDown={(e) => {
            if (isRenaming) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onSelect(conv.id);
            }
          }}
          className={cn(
            "group relative rounded-lg cursor-pointer transition-all duration-150 outline-none focus-visible:ring-2 focus-visible:ring-[#003D82]/40",
            // Conv du dossier courant : léger voile bleu (couleur « dossier/lien ») au
            // repos + au survol → repère discret, sans trait. L'actif garde le gris.
            isActive
              ? "bg-[#E5E5E3] shadow-sm"
              : accent
                ? "bg-[#003D82]/[0.05] hover:bg-[#003D82]/[0.09]"
                : "hover:bg-[#F0F0EE]"
          )}
          onClick={() => { if (!isRenaming) onSelect(conv.id); }}
        >
          <div className={cn("flex items-start gap-2", roomy ? "px-3 py-2" : "px-2.5 py-1.5")}>
            {isRenaming ? (
              <input
                ref={rename.inputRef}
                value={rename.value}
                onChange={(e) => rename.setValue(e.target.value)}
                onKeyDown={rename.handleKeyDown}
                onBlur={rename.handleBlur}
                onClick={(e) => e.stopPropagation()}
                disabled={rename.isSaving}
                maxLength={255}
                aria-label="Renommer la conversation"
                className="flex-1 min-w-0 text-[13px] leading-tight font-normal text-[#1B1B1E] bg-white border border-[#003D82]/40 rounded px-1.5 py-0.5 outline-none focus:border-[#003D82]"
              />
            ) : (
              <>
                {/* Deux lignes : titre (tronqué) + pastille de statut live, puis le dossier
                    TOUJOURS visible en dessous (icône serviette + nom). */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    {conv.isPinned && !conv.isArchived && (
                      <Pin className="h-3 w-3 flex-shrink-0 text-grey-500" aria-label="Conversation épinglée" />
                    )}
                    <p className="min-w-0 flex-1 truncate text-[13px] leading-tight font-normal text-[#1B1B1E]">
                      {displayTitle}
                    </p>
                    {/* Le crayon documente l'éditeur, le point documente le tour :
                        deux informations indépendantes, jamais deux points. */}
                    {canvasPresentation && (
                      <PenLine
                        className={canvasPresentation.className}
                        // `role="img"` est obligatoire : sur un <svg> sans rôle
                        // explicite, `aria-label` n'est pas exposé de façon
                        // fiable (VoiceOver le laisse tomber) — et ce badge est
                        // le seul des quatre à porter un nom accessible. Un test
                        // Playwright passerait sans, d'où ce commentaire.
                        role="img"
                        aria-label={canvasPresentation.ariaLabel}
                      />
                    )}
                    {badge === "waiting_action" && (
                      <span className="h-2 w-2 flex-shrink-0 rounded-full bg-orange-400 animate-pulse" role="img" aria-label="Action requise de votre part" />
                    )}
                    {badge === "streaming" && (
                      <span className="h-2 w-2 flex-shrink-0 rounded-full bg-charcoal animate-pulse" role="img" aria-label="Tour agent en cours" />
                    )}
                    {badge === "connecting" && (
                      <span className="h-2 w-2 flex-shrink-0 rounded-full bg-gray-400 animate-pulse" role="img" aria-label="Connexion de l’agent en cours" />
                    )}
                    {/* No 'error' dot — computeBadges never emits 'error' (2026-06-26). */}
                  </div>
                  {/* 2e ligne TOUJOURS présente → hauteur de box uniforme (sinon la date en
                      absolute débordait sous les lignes courtes). Dossier (serviette + nom)
                      ou « Général » muet quand la conv n'est rattachée à aucun dossier. */}
                  <div className="mt-0.5 flex items-center gap-1 text-[11px] leading-tight">
                    {conv.caseTitle ? (
                      <>
                        <Briefcase className="h-3 w-3 flex-shrink-0 text-grey-500" />
                        <span className="truncate text-grey-500">{conv.caseTitle}</span>
                      </>
                    ) : (
                      <span className="text-grey-400">Général</span>
                    )}
                  </div>
                </div>

                {/* Avec qui c'est partagé — avatars minuscules À GAUCHE du glyphe
                    source (demande produit 2026-07-07). Rien si privé sans partage. */}
                <ConversationShareGlyph
                  visibility={conv.visibility}
                  myRole={conv.my_role}
                  ownerId={conv.ownerId}
                  currentUserId={currentUserId}
                  shares={conv.shares}
                  members={members}
                />

                {/* The stable action slot prevents title overlap. Pointer hover and
                    keyboard focus swap the source badge for the menu; coarse pointers
                    keep the 32px menu target visible because they have no hover state. */}
                <div className="relative h-8 w-8 flex items-center justify-center flex-shrink-0">
                  <ConversationSourceBadge
                    sourceSystem={conv.sourceSystem}
                    className={cn(
                      "absolute inset-0 flex items-center justify-center transition-opacity",
                      (menuOpen || shareOpen)
                        ? "opacity-0"
                        : "opacity-100 group-hover:opacity-0 group-focus-within:opacity-0 max-sm:opacity-0 [@media(pointer:coarse)]:opacity-0",
                    )}
                  />
                  <div
                    className={cn(
                      "flex items-center gap-0.5 transition-opacity",
                      (menuOpen || shareOpen)
                        ? "opacity-100"
                        : "opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 max-sm:opacity-100 [@media(pointer:coarse)]:opacity-100",
                    )}
                  >
                    <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
                      {/* Le Popover de partage s'ancre au bouton ⋮ ; il s'ouvre via l'item
                          « Partager » ci-dessous (owner-only), pas par un clic sur le ⋮. */}
                      <ShareConversationPopover
                        conversationId={conv.id}
                        ownerId={conv.ownerId ?? ''}
                        currentVisibility={conv.visibility ?? 'private'}
                        open={shareOpen}
                        onOpenChange={setShareOpen}
                      >
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-[#666666] hover:text-[#1B1B1E] hover:bg-[#E5E5E3] data-[state=open]:opacity-100"
                            onClick={(e) => e.stopPropagation()}
                            aria-label={`Actions pour la conversation ${displayTitle}`}
                            title="Actions de la conversation"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                      </ShareConversationPopover>
                      <DropdownMenuContent
                        align="end"
                        className="w-40"
                        aria-label={`Actions de la conversation ${displayTitle}`}
                      >
                        {!conv.isArchived && (
                          <DropdownMenuItem
                            onClick={(event) => {
                              event.stopPropagation();
                              void onPinChange(conv.id, !conv.isPinned);
                            }}
                            className="text-xs text-charcoal focus:bg-grey-100 focus:text-charcoal"
                          >
                            {conv.isPinned
                              ? <PinOff className="w-3.5 h-3.5 mr-2" aria-hidden />
                              : <Pin className="w-3.5 h-3.5 mr-2" aria-hidden />}
                            {conv.isPinned ? 'Désépingler' : 'Épingler'}
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          onClick={(event) => {
                            event.stopPropagation();
                            void onArchiveChange(conv.id, !conv.isArchived);
                          }}
                          className="text-xs text-charcoal focus:bg-grey-100 focus:text-charcoal"
                        >
                          {conv.isArchived
                            ? <ArchiveRestore className="w-3.5 h-3.5 mr-2" aria-hidden />
                            : <Archive className="w-3.5 h-3.5 mr-2" aria-hidden />}
                          {conv.isArchived ? 'Désarchiver' : 'Archiver'}
                        </DropdownMenuItem>
                        {ownerActionDisabledReason ? (
                          <ReadOnlyConversationMenuItem
                            icon={<Pencil className="w-3.5 h-3.5 mr-2" />}
                            label="Renommer"
                            reason={ownerActionDisabledReason}
                          />
                        ) : (
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              onStartRename(conv.id);
                            }}
                            className="text-xs text-charcoal focus:bg-grey-100 focus:text-charcoal"
                          >
                            <Pencil className="w-3.5 h-3.5 mr-2" />
                            Renommer
                          </DropdownMenuItem>
                        )}
                        {conv.my_role === 'owner' && (
                          <DropdownMenuItem
                            onClick={(e) => {
                              // Sans stopPropagation, le clic bubble (à travers l'arbre React —
                              // DropdownMenuContent est porté hors du DOM de la ligne mais reste
                              // un descendant React) jusqu'au onClick de la ligne entière, qui
                              // navigue vers la conversation au lieu d'ouvrir le partage (bug
                              // constaté en test live, 2026-07-07). Même pattern que Renommer/
                              // Supprimer ci-dessus/dessous.
                              e.stopPropagation();
                            }}
                            onSelect={() => {
                              // PAS de `e.preventDefault()` : sur un item de menu Radix, ça
                              // empêche le DropdownMenu de se fermer (comportement documenté
                              // prévu pour les items checkbox) — il restait "ouvert" en
                              // arrière-plan et entrait en conflit de focus avec le Popover dès
                              // qu'on cliquait dans sa recherche (le panneau disparaissait,
                              // signalé par l'utilisateur 2026-07-07). Le menu se ferme
                              // normalement ; on diffère juste l'ouverture du Popover au tick
                              // suivant pour éviter l'ouverture/fermeture dans la même passe.
                              setTimeout(() => setShareOpen(true), 0);
                            }}
                            className="text-xs text-charcoal focus:bg-grey-100 focus:text-charcoal"
                          >
                            <Share2 className="w-3.5 h-3.5 mr-2" />
                            Partager
                          </DropdownMenuItem>
                        )}
                        {ownerActionDisabledReason ? (
                          <ReadOnlyConversationMenuItem
                            icon={<Trash2 className="w-3.5 h-3.5 mr-2" />}
                            label="Supprimer"
                            reason={ownerActionDisabledReason}
                            destructive
                          />
                        ) : (
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              onRequestDelete(conv.id);
                            }}
                            className="text-xs text-red-600 focus:bg-red-50 focus:text-red-700"
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-2" />
                            Supprimer
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <span className="absolute top-full right-0 mt-1 whitespace-nowrap text-[10px] leading-none text-grey-400 opacity-0 transition-opacity group-hover:opacity-100">
                    {formatShortDate(conv.lastMessageAt || conv.createdAt)}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      </TooltipTrigger>
      <TooltipContent
        side="right"
        className="max-w-[300px] text-xs"
        sideOffset={8}
      >
        {/* Dossier (inline dans la ligne) et date (sous le glyphe au survol) sont désormais
            visibles directement → le tooltip ne porte plus que le nom complet de la conv. */}
        <span className="font-medium text-charcoal">{displayTitle}</span>
      </TooltipContent>
    </Tooltip>
  );
});
